import { Injectable } from "@angular/core";
import {
  IUserProfile,
  ME_USER,
} from "app/bundles/authorize/shared/user-profile";
import { UserProfileService } from "app/bundles/authorize/shared/user-profile.service";
import * as _ from "lodash-es";
import { tap } from "rxjs";
import { CurrentUserApi } from "../api-services/current-user-api/current-user-api.models";
import {
  IAllowableActivity,
  IAllowableActivityDTO,
  IAllowableResponsible,
  IAllowableResponsibleDTO,
} from "../models/currentUser";

type IAllowableGroupFlattened = {
  groupId: number;
  groupName: string;
};

type IAllowableGroupByIdFlattened = {
  [K: string]: IAllowableGroupFlattened;
};

type IAllowableBoroughFlattened = {
  groupById: IAllowableGroupByIdFlattened;
  boroughId: number;
  boroughName: string;
};

type IAllowableBoroughByIdFlattened = {
  [K: string]: IAllowableBoroughFlattened;
};

type IAllowableActivityFlattened = {
  activityCode: string;
  activityName: string;
  boroughsById?: IAllowableBoroughByIdFlattened;
};

type IAllowableActivityByIdFlattened = {
  [K: string]: IAllowableActivityFlattened;
};

@Injectable()
export class CurrentUserService {
  private allowableResponsibleUsers: IAllowableResponsibleDTO[] = [];
  private allowableActivities: IAllowableActivityDTO[] = [];
  private userProfile: IUserProfile;
  private userBoroughId: number;
  private allowableActivitiesByCode: IAllowableActivityByIdFlattened;

  constructor(
    private currentUserApiService: CurrentUserApi,
    private userProfileService: UserProfileService,
  ) {
    this.userProfileService.userProfile$.subscribe(
      (user) => (this.userProfile = user),
    );
  }

  public allowableResponsibleUsersFetched(): boolean {
    return this.allowableResponsibleUsers?.length > 0;
  }

  public allowableActivitiesFetched(): boolean {
    return this.allowableActivities?.length > 0;
  }

  public setAllowableResponsibleUsers() {
    return this.currentUserApiService.getAllowableResponsibles().pipe(
      tap((res) => {
        this.allowableResponsibleUsers = this.formatResponsibleUsers(res);
        this.setUserBorougId();
      }),
    );
  }

  private buildAllowableActivitiesByCode(res: IAllowableActivityDTO[]) {
    const allowableActivitiesByCode = res.reduce(
      (
        acc: IAllowableActivityByIdFlattened,
        cur,
      ): IAllowableActivityByIdFlattened => {
        acc = acc || {};
        acc[cur.code] = {
          activityCode: cur.code,
          activityName: cur.name,
          boroughsById: cur.boroughs.reduce((acc2, cur2) => {
            acc2 = acc2 || {};
            acc2[cur2.id] = {
              boroughId: cur2.id,
              boroughName: cur2?.name,
              groupById: cur2.units.reduce((accUnit, curUnit) => {
                accUnit = accUnit || {};
                if (!curUnit?.group?.id) {
                  return accUnit;
                }
                accUnit[curUnit.group.id] = {
                  groupId: curUnit.group.id,
                  groupName: curUnit.group.name,
                };
                return accUnit;
              }, {} as IAllowableGroupByIdFlattened),
            };
            return acc2;
          }, {} as IAllowableBoroughByIdFlattened),
        };
        return acc;
      },
      {} as IAllowableActivityByIdFlattened,
    );
    return allowableActivitiesByCode;
  }

  public setAllowableActivities() {
    return this.currentUserApiService.getAllowableActivities().pipe(
      tap((res) => (this.allowableActivities = res)),
      tap(
        (res) =>
          (this.allowableActivitiesByCode =
            this.buildAllowableActivitiesByCode(res)),
      ),
    );
  }

  public setUserBorougId(): void {
    const userBorough = this.allowableResponsibleUsers.find(
      (responsibleDTO) =>
        responsibleDTO.userCode.toUpperCase() ===
        this.userProfile.userName.toUpperCase(),
    );
    userBorough ? (this.userBoroughId = userBorough.belongsToBoroughId) : null;
  }

  public getUserBoroughId(): number {
    if (!this.userBoroughId) {
      this.setUserBorougId();
    }
    return this.userBoroughId;
  }

  public getAllowableUsersForAGroup(groupId: number): IAllowableResponsible[] {
    let userMe: IAllowableResponsible;
    let allowableUserList = this.allowableResponsibleUsers
      .map((responsibleDTO) => {
        const foundIndex = responsibleDTO.groups.findIndex(
          (group) => group.id === groupId,
        );
        if (foundIndex > -1) {
          if (
            responsibleDTO.userCode.toUpperCase() ===
            this.userProfile.userName.toUpperCase()
          ) {
            userMe = {
              firstName: `${responsibleDTO.firstName} ${ME_USER}`,
              lastName: responsibleDTO.lastName,
              id: responsibleDTO.id,
              userCode: responsibleDTO.userCode,
              belongsToBoroughId: responsibleDTO.belongsToBoroughId,
            };
          } else {
            return {
              firstName: responsibleDTO.firstName,
              lastName: responsibleDTO.lastName,
              id: responsibleDTO.id,
              userCode: responsibleDTO.userCode,
              belongsToBoroughId: responsibleDTO.belongsToBoroughId,
            };
          }
        }
      })
      .filter((el) => el !== undefined);
    allowableUserList = _.orderBy(
      allowableUserList,
      ["lastName", "firstName"],
      ["asc", "asc"],
    );
    if (userMe) {
      allowableUserList.unshift(userMe);
    }
    return allowableUserList;
  }

  public getAllowableActivities4BelongingBorough(): IAllowableActivity[] {
    const allowableActivityList: IAllowableActivity[] = [];
    this.allowableActivities
      .map((activityDTO) => {
        activityDTO.boroughs.map((boroughDTO) => {
          if (boroughDTO.id === this.userBoroughId) {
            boroughDTO.units.map((unitDTO) => {
              unitDTO.templates.map((template) => {
                allowableActivityList.push({
                  id: activityDTO.id,
                  code: activityDTO.code,
                  name: activityDTO.name,
                  type: activityDTO.type === "ACTU" ? "Universelle" : "Locale",
                  hasForm: activityDTO.hasForm,
                  template,
                  borough: { id: boroughDTO.id, name: boroughDTO.name },
                  responsibleUnit: {
                    id: unitDTO.responsible.id,
                    name: unitDTO.responsible.name,
                  },
                  group: { id: unitDTO.group.id, name: unitDTO.group.name },
                });
              });
            });
          }
        });
      })
      .filter((el) => el !== undefined);

    allowableActivityList.sort((a, b) => {
      const r = a.name.localeCompare(b.name);
      if (r === 0) {
        return a.template.name.localeCompare(b.template.name);
      }
      return r;
    });
    return allowableActivityList;
  }

  public getAllowableActivitiesByCode(): IAllowableActivityByIdFlattened {
    return this.allowableActivitiesByCode;
  }

  public hasActivityForm(activityCode: string): boolean {
    return this.allowableActivities.find(
      (activity) => activity.code === activityCode,
    ).hasForm;
  }

  public isUserSameBoroughAsRequest(requestBoroughId: number): boolean {
    return this.userBoroughId === requestBoroughId;
  }

  private formatResponsibleUsers(
    allowableResponsibleUsers: IAllowableResponsibleDTO[],
  ) {
    allowableResponsibleUsers.map((user) => {
      user.firstName = user.firstName.toUpperCase();
      user.lastName = user.lastName.toUpperCase();
    });
    return allowableResponsibleUsers;
  }
}
